import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { FkIcon } from "./Icons";
import { useDismiss } from "./useDismiss";

/** Адреса CRM БРАСОЛ: профиль и смена пароля живут там (отдельного пароля у SMS-центра нет). */
export const CRM_URL = "https://crm.brasol.ru/";
export const CRM_PROFILE_URL = "https://crm.brasol.ru/profile";

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "БМ";
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * Меню аккаунта в шапке: имя, роль, юрлицо; «Профиль» и «Сменить пароль» — в CRM
 * (новая вкладка), «В CRM БРАСОЛ», «Выйти». `defaultOpen` — для тестов разметки.
 */
export function AccountMenu({
  name,
  roleLabel,
  company,
  onLogout,
  defaultOpen = false,
}: {
  name: string;
  /** ROLE_LABEL: «Управляющая компания» | «Сотрудник УК» | «Кабинет партнёра». */
  roleLabel: string;
  /** Юрлицо или «Юридических лиц: N». */
  company?: string;
  onLogout: () => void;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const ref = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, ref, close);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  const shownName = name.trim() || "Пользователь";
  return (
    <div className="fk-pop-anchor" ref={ref}>
      <button
        type="button"
        className={`fk-account-btn${open ? " is-open" : ""}`}
        title={shownName}
        aria-label={`Аккаунт: ${shownName}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="fk-account-panel"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="fk-avatar" aria-hidden="true">
          {initials(name)}
        </span>
        <FkIcon name="chevD" size={13} className="fk-account-chev" />
      </button>
      {open && (
        <div
          id="fk-account-panel"
          className="fk-pop fk-account-pop"
          role="dialog"
          aria-label="Аккаунт"
        >
          <div className="fk-account-head">
            <div className="fk-account-name">{shownName}</div>
            <div className="fk-account-meta">
              <span className="fk-account-role">{roleLabel}</span>
              {company && <span className="fk-account-company">{company}</span>}
            </div>
          </div>
          <a
            className="fk-pop-item"
            href={CRM_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
          >
            <FkIcon name="user" size={15} />
            <span>Профиль</span>
            <FkIcon name="external" size={13} className="fk-pop-item-ext" />
          </a>
          <a
            className="fk-pop-item"
            href={CRM_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
          >
            <FkIcon name="key" size={15} />
            <span>Сменить пароль</span>
            <FkIcon name="external" size={13} className="fk-pop-item-ext" />
          </a>
          <a
            className="fk-pop-item"
            href={CRM_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
          >
            <FkIcon name="back" size={15} />
            <span>В CRM БРАСОЛ</span>
            <FkIcon name="external" size={13} className="fk-pop-item-ext" />
          </a>
          <div className="fk-pop-sep" role="separator" />
          <button
            type="button"
            className="fk-pop-item fk-pop-danger"
            onClick={() => {
              close();
              onLogout();
            }}
          >
            <FkIcon name="out" size={15} />
            <span>Выйти</span>
          </button>
        </div>
      )}
    </div>
  );
}
